// src/components/post_course/PostCourseViewer.jsx
import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import "../messages/messages.css";
import "./courseprep.css";

const QUESTIONS_PER_ATTEMPT = 3;

/** =========================
 *  HELPERS

 *  ========================= */
const getLearnerName = (u) => u?.firstName || u?.first_name || u?.name || "Artist";

const isUnlockedOnStartDate = (startDate) => {
  if (!startDate) return false;
  const d = new Date(startDate);
  if (Number.isNaN(d.getTime())) return false;

  // unlock on Day 1 at local midnight
  const startLocalMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  return Date.now() >= startLocalMidnight;
};

const normalizeProgressMap = (enrollment) => {
  const map = {};
  (enrollment?.postCourseProgress || []).forEach((p) => {
    if (!p?.sectionId) return;
    map[String(p.sectionId)] = p;
  });
  return map;
};

const getStatusLabel = (statusRaw) => {
  const status = String(statusRaw || "").toLowerCase();
  if (status === "passed") return "Passed";
  if (status === "submitted") return "Submitted — awaiting review";
  if (status === "changes_requested") return "Changes requested";
  if (status === "in_progress") return "In progress";
  return "Locked";
};

const formatLocalDate = (iso) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleString();
  } catch {
    return "";
  }
};

const getCourseTitle = (course, module) => course?.title || module?.title || "Post-Course";
const safeUrlLabel = (label, url) => {
  if (label) return label;
  try {
    const u = new URL(url);
    return u.pathname.split("/").pop() || "Resource";
  } catch {
    return "Resource";
  }
};

/* =========================
   INSTRUCTORS (same vibe as prep)
   ========================= */
const INSTRUCTORS = {
  nikky: {
    name: "INSTRUCTOR NIKKY",
    image: "https://cosacademyassets.s3.us-east-2.amazonaws.com/profile/IMG_1282.PNG",
  },
  sean: {
    name: "INSTRUCTOR SEAN",
    image: "https://cosacademyassets.s3.us-east-2.amazonaws.com/profile/IMG_1672.PNG",
  },
};

const getInstructorForSection = (sectionIndex) => {
  if (!sectionIndex) return INSTRUCTORS.nikky;
  return sectionIndex % 2 === 1 ? INSTRUCTORS.nikky : INSTRUCTORS.sean;
};

/* =========================
   TYPEWRITER TEXT HELPER
   ========================= */
const AnimatedText = ({ text, speed = 36, chunkSize = 3, delay = 0 }) => {
  const [displayed, setDisplayed] = useState("");
  const intervalRef = useRef(null);
  const delayRef = useRef(null);

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (delayRef.current) {
      clearTimeout(delayRef.current);
      delayRef.current = null;
    }

    if (!text) {
      setDisplayed("");
      return;
    }

    let index = 0;
    setDisplayed("");

    const startTyping = () => {
      intervalRef.current = setInterval(() => {
        index += chunkSize;
        setDisplayed(text.slice(0, index));

        if (index >= text.length) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }, speed);
    };

    if (delay > 0) {
      delayRef.current = setTimeout(startTyping, delay);
    } else {
      startTyping();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (delayRef.current) {
        clearTimeout(delayRef.current);
        delayRef.current = null;
      }
    };
  }, [text, speed, chunkSize, delay]);

  const isDone = displayed && text && displayed.length >= text.length;

  return (
    <span className="animated-text">
      {displayed}
      {!isDone && <span className="animated-text-cursor">|</span>}
    </span>
  );
};

/** =========================
 *  LIGHTBOX (image/video)
 *  ========================= */
const Lightbox = ({ open, items, index, onClose, onPrev, onNext, enforceMuted }) => {
  const [zoom, setZoom] = useState(1);
  const ZOOM_MIN = 1;
  const ZOOM_MAX = 3;
  const ZOOM_STEP = 0.5;
  const zoomPct = Math.round(zoom * 100);
  const current = items?.[index];
  const isImage = current?.type !== "video";

  useEffect(() => {
    if (!open) return;
    setZoom(1);
  }, [open, index]);

  const handleOpenFull = () => {
    if (!current?.src) return;
    window.open(current.src, "_blank", "noopener,noreferrer");
  };

  if (!open) return null;

  return (
    <>
      <div className="course-lightbox-backdrop open" onClick={onClose} />
      <div
        className="course-lightbox-modal open"
        role="dialog"
        aria-modal="true"
        aria-label="Media viewer"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="course-lightbox-header">
          <div className="course-lightbox-title">
            {items?.length ? `Media ${index + 1} of ${items.length}` : "Media"}
          </div>
          <div className="course-lightbox-actions">
            {isImage && (
              <button
                type="button"
                className="course-lightbox-actionbtn"
                onClick={handleOpenFull}
                aria-label="Open image in full screen"
                title="Open full screen"
              >
                Full screen
              </button>
            )}
            <button type="button" className="course-lightbox-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="course-lightbox-body">
          {items?.length > 1 && (
            <>
              <button
                type="button"
                className="course-lightbox-nav course-lightbox-nav-left"
                onClick={onPrev}
                aria-label="Previous"
              >
                ‹
              </button>
              <button
                type="button"
                className="course-lightbox-nav course-lightbox-nav-right"
                onClick={onNext}
                aria-label="Next"
              >
                ›
              </button>
            </>
          )}

          {current?.type === "video" ? (
            <video
              src={current.src}
              className={`course-lightbox-image ${current?.muted ? "video-no-volume" : ""}`}
              controls
              playsInline
              muted
              onVolumeChange={enforceMuted}
              onLoadedMetadata={enforceMuted}
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          ) : (
            <div className="course-lightbox-media" aria-label="Zoomable image">
              <img
                src={current?.src || ""}
                alt={current?.alt || ""}
                className="course-lightbox-image"
                draggable={false}
                loading="eager"
                decoding="async"
                style={{ width: `${zoomPct}%` }}
                onDoubleClick={handleOpenFull}
              />
            </div>
          )}
          {isImage && (
            <div className="course-lightbox-zoom">
              <button
                type="button"
                className="course-lightbox-zoombtn"
                onClick={() => setZoom((z) => Math.max(ZOOM_MIN, z - ZOOM_STEP))}
                disabled={zoom <= ZOOM_MIN}
                aria-label="Zoom out"
                title="Zoom out"
              >
                −
              </button>
              <button
                type="button"
                className="course-lightbox-zoombtn"
                onClick={() => setZoom(1)}
                disabled={zoom === 1}
                aria-label="Reset zoom"
                title="Reset zoom"
              >
                {zoomPct}%
              </button>
              <button
                type="button"
                className="course-lightbox-zoombtn"
                onClick={() => setZoom((z) => Math.min(ZOOM_MAX, z + ZOOM_STEP))}
                disabled={zoom >= ZOOM_MAX}
                aria-label="Zoom in"
                title="Zoom in"
              >
                +
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

/** =========================
 *  POST-COURSE QUIZ (styled like prep)
 *  ========================= */
const SectionQuiz = ({
  sectionId,
  quiz,
  quizQuestions,
  quizPassed,
  learnerName,
  busy,
  onSubmitAttempt, // (payload) => Promise<void>
  onPassed, // () => void
}) => {
  const [attempt, setAttempt] = useState(0);
  const [picked, setPicked] = useState([]); // question indexes in bank
  const [answers, setAnswers] = useState({}); // { localIdx: selectedIndex }
  const [submitted, setSubmitted] = useState(false);
  const [allCorrect, setAllCorrect] = useState(false);
  const [error, setError] = useState("");
  const [localPassed, setLocalPassed] = useState(false);

  useEffect(() => {
    setAttempt(0);
    setPicked([]);
    setAnswers({});
    setSubmitted(false);
    setAllCorrect(false);
    setError("");
    setLocalPassed(false);
  }, [sectionId]);

  const getCorrectIndex = useCallback((q) => {
    if (!q) return undefined;
    if (Array.isArray(q.correctOptionIndexes) && q.correctOptionIndexes.length) {
      const idx = Number(q.correctOptionIndexes[0]);
      if (Number.isFinite(idx)) return idx;
    }
    const fallback = Number(q.answerIndex);
    return Number.isFinite(fallback) ? fallback : undefined;
  }, []);

  const startAttempt = useCallback(() => {
    const bank = quizQuestions || [];
    const indices = bank.map((_, idx) => idx);
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j], indices[i]];
    }
    const nextPicked = indices.slice(0, Math.min(QUESTIONS_PER_ATTEMPT, indices.length));
    setPicked(nextPicked);
    setAnswers({});
    setSubmitted(false);
    setAllCorrect(false);
    setError("");
  }, [quizQuestions]);

  const questions = useMemo(() => {
    if (!picked.length) return [];
    return picked.map((bankIdx) => ({ ...(quizQuestions?.[bankIdx] || {}), __bankIndex: bankIdx }));
  }, [picked, quizQuestions]);

  const handleSelect = (qIdx, optIdx) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [qIdx]: optIdx }));
    setError("");
  };

  const scoreResult = useMemo(() => {
    if (!questions.length) return { score: 0, total: 0, passed: false };
    let score = 0;
    questions.forEach((q, idx) => {
      const selected = answers[idx];
      const correctIndex = getCorrectIndex(q);
      if (Number.isFinite(selected) && Number.isFinite(correctIndex) && selected === correctIndex) score += 1;
    });
    const total = questions.length;
    const passed = total > 0 && score === total; // 100% required
    return { score, total, passed };
  }, [questions, answers, getCorrectIndex]);

  const submit = async () => {
    if (quizPassed || localPassed) return;

    if (submitted && !allCorrect) {
      startAttempt();
      return;
    }

    if (!picked.length) {
      startAttempt();
      return;
    }

    const allAnswered = questions.every((_, idx) => Number.isFinite(Number(answers[idx])));
    if (!allAnswered) {
      setError("Please answer all questions before submitting.");
      return;
    }

    const payload = {
      questionIndexes: questions.map((q) => q.__bankIndex),
      answers: questions.map((q, idx) => ({
        questionIndex: q.__bankIndex,
        selectedIndex: Number(answers[idx]),
      })),
      score: scoreResult.score,
      total: scoreResult.total,
    };

    try {
      await onSubmitAttempt(payload);
    } catch (e) {
      console.error(e);
      setError("Quiz submission failed. Please try again.");
      return;
    }

    setSubmitted(true);
    setAllCorrect(scoreResult.passed);
    if (scoreResult.passed) {
      setLocalPassed(true);
      onPassed && onPassed();
    }

    if (!scoreResult.passed) {
      // next attempt uses new questions
      setAttempt((a) => a + 1);
    }
  };

  return (
    <div className="course-section-quiz" style={{ marginTop: 18 }}>
      <div className="quiz-header">
        <h3>{quiz?.title || "Knowledge Check"}</h3>
        <p>
          {quizPassed || localPassed
            ? "You’ve already passed this quiz — this is a refresher."
            : "You must score 100% to count this quiz as passed."}
        </p>
      </div>

      {!quizPassed && !localPassed && !picked.length && (
        <div className="quiz-instructor-note">
          Let&apos;s see what stuck from this section, {learnerName}. Answer how you truly understand it.
        </div>
      )}

      {picked.length > 0 &&
        questions.map((q, qIdx) => {
          const selectedIndex = answers[qIdx];
          const correctIndex = getCorrectIndex(q);

          return (
            <div key={`${sectionId}-${q.__bankIndex}-${qIdx}-${attempt}`} className="quiz-question-card">
              <div className="quiz-question-number">Question {qIdx + 1}</div>
              <div className="quiz-prompt">{q.q || q.prompt}</div>

              <div className="quiz-options-grid">
                {(q.options || []).map((opt, optIdx) => {
                  const isSelected = selectedIndex === optIdx;
                  const isCorrect = submitted && Number.isFinite(correctIndex) && optIdx === correctIndex;
                  const isIncorrect = submitted && isSelected && !isCorrect;

                  const optionClass = [
                    "quiz-option",
                    isSelected && "selected",
                    isCorrect && "correct",
                    isIncorrect && "incorrect",
                    submitted && "disabled",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <button
                      key={optIdx}
                      type="button"
                      className={optionClass}
                      onClick={() => handleSelect(qIdx, optIdx)}
                      disabled={busy || quizPassed}
                    >
                      <span className="quiz-option-label">{String.fromCharCode(65 + optIdx)}.</span>
                      <span className="quiz-option-text">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}

      <div className="quiz-footer">
        {error && <div className="quiz-error">{error}</div>}

        {(quizPassed || localPassed) && (
          <div className="quiz-score-pill">Passed ✅</div>
        )}

        {!quizPassed && !localPassed && submitted && picked.length > 0 && (
          <div className="quiz-score-pill">
            {scoreResult.score}/{scoreResult.total} correct
          </div>
        )}

        {!quizPassed && !localPassed && submitted && allCorrect && (
          <div className="quiz-success-pill">✅ Perfect — quiz passed.</div>
        )}

        {!quizPassed && !localPassed && submitted && !allCorrect && (
          <div className="quiz-instructor-note coach quiz-fail-spotlight" role="alert" aria-live="assertive">
            <div className="quiz-fail-header">
              <span className="quiz-fail-icon" aria-hidden="true">
                ⚠️
              </span>
              <div className="quiz-fail-title">Not quite — you’ll need a perfect score to pass.</div>
            </div>

            <div className="quiz-fail-banner">
              <span className="quiz-fail-score">
                {scoreResult.score}/{scoreResult.total}
              </span>
              <span className="quiz-fail-requirement">
                Required: <strong>100%</strong>
              </span>
            </div>

            <p className="quiz-fail-copy">
              You&apos;re close. Review the section, then try again with a new set of questions.
            </p>
          </div>
        )}

        {!quizPassed && !localPassed && (
          <button type="button" className="quiz-submit-button" disabled={busy} onClick={submit}>
            {busy ? "Submitting…" : submitted && !allCorrect ? "Try Again" : picked.length ? "Submit Quiz" : "Start Quiz"}
          </button>
        )}
      </div>
    </div>
  );
};

/** =========================
 *  MAIN COMPONENT
 *  ========================= */
const PostCourseViewer = ({
  course,
  currentUser,
  enrollment,
  module,
  onSubmitSectionAssignment, // (enrollmentId, sectionId, { text, files }) => Promise
  onReviewSectionAssignment, // (enrollmentId, sectionId, { action, feedback }) => Promise
  onMarkSectionPassed, // (enrollmentId, sectionId) => Promise
  onSubmitPostCourseQuizAttempt, // (enrollmentId, sectionId, { questionIndexes, answers, score, total }) => Promise
  onRefreshEnrollments,
}) => {
  const learnerName = getLearnerName(currentUser);
  const courseTitle = getCourseTitle(course, module);
  const heroTag = "POST-COURSE MODULE";

  const isStaff = currentUser?.role === "master" || currentUser?.role === "grandmaster";
  const unlocked = useMemo(() => isUnlockedOnStartDate(enrollment?.startDate), [enrollment?.startDate]);
  const prepCompleted = !!enrollment?.prepCompleted;
  const canAccess = unlocked && prepCompleted;

  const unlockCopy = enrollment?.startDate
    ? `Unlocks on your class Day 1: ${new Date(enrollment.startDate).toLocaleDateString()}`
    : "Unlocks on your class Day 1.";
  const prepCopy = "Complete your prep course to unlock post-course assignments.";

  const sections = useMemo(() => {
    const list = module?.sections || [];
    return list.slice().sort((a, b) => (a.index || 0) - (b.index || 0));
  }, [module]);

  const progressMap = useMemo(() => normalizeProgressMap(enrollment), [enrollment]);

  const completedSectionIds = useMemo(() => {
    return sections
      .filter((s) => String(progressMap[String(s.id)]?.status || "").toLowerCase() === "passed")
      .map((s) => String(s.id));
  }, [sections, progressMap]);

  const totalSections = sections.length;
  const completedCount = completedSectionIds.length;
  const overallProgressPct = totalSections > 0 ? Math.round((completedCount / totalSections) * 100) : 0;
  const isComplete = totalSections > 0 && completedCount === totalSections;

  /** Sidebar + active section */
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const didAutoSelectRef = useRef(false);

  useEffect(() => {
    didAutoSelectRef.current = false;
    setActiveSectionIndex(0);
  }, [enrollment?._id, module?.title, course?.key]);

  const isBlockedStatus = useCallback(
    (status) => {
      const s = String(status || "").toLowerCase();
      return s === "submitted" || s === "changes_requested";
    },
    []
  );

  const nextIncompleteIdx = useMemo(() => {
    if (!canAccess || !sections.length) return -1;

    const firstChangesIdx = sections.findIndex((s) => {
      const prog = progressMap[String(s.id)];
      const status = String(prog?.status || "").toLowerCase();
      return status === "changes_requested";
    });
    if (firstChangesIdx >= 0) return firstChangesIdx;

    const idx = sections.findIndex((s) => {
      const prog = progressMap[String(s.id)];
      const status = String(prog?.status || "in_progress").toLowerCase();
      const isPassed = status === "passed";
      if (isPassed) return false;
      if (isBlockedStatus(status)) return false;
      return true;
    });
    return idx === -1 ? Math.max(0, sections.length - 1) : idx;
  }, [canAccess, sections, progressMap, isBlockedStatus]);

  useEffect(() => {
    if (!sections.length) return;
    if (didAutoSelectRef.current) return;

    if (!canAccess) {
      setActiveSectionIndex(0);
      didAutoSelectRef.current = true;
      return;
    }

    setActiveSectionIndex(nextIncompleteIdx >= 0 ? nextIncompleteIdx : 0);
    didAutoSelectRef.current = true;
  }, [sections, canAccess, nextIncompleteIdx]);

  const activeSection = sections[activeSectionIndex] || null;
  const blocks = activeSection?.blocks || [];
  const activeProgress = activeSection ? progressMap[String(activeSection.id)] : null;

  const activeStatus = String(activeProgress?.status || (canAccess ? "in_progress" : "locked")).toLowerCase();
  const isActivePassed = activeStatus === "passed";
  const isChangesRequested = activeStatus === "changes_requested";

  const activeInstructor = activeSection ? getInstructorForSection(activeSection.index) : INSTRUCTORS.nikky;
  const isLipCourse = String(course?.key || "").toLowerCase() === "lip_blush";
  const heroSpeech =
    "These sections reinforce what you learned in class. You can move ahead anytime — sections only become PASSED once you’ve passed the quiz and/or gained approval for your assignments.";

  // Track last-known status to avoid auto-advancing when revisiting passed sections
  const lastStatusBySectionRef = useRef({});

  useEffect(() => {
    const next = { ...(lastStatusBySectionRef.current || {}) };
    sections.forEach((s) => {
      const id = String(s?.id || "");
      if (!id) return;
      if (completedSectionIds.includes(id) && !next[id]) next[id] = "passed";
    });
    lastStatusBySectionRef.current = next;
  }, [sections, completedSectionIds]);

  const getStatusForSection = useCallback(
    (section) => {
      if (!section) return "locked";
      const prog = progressMap[String(section.id)];
      return String(prog?.status || (canAccess ? "in_progress" : "locked")).toLowerCase();
    },
    [progressMap, canAccess]
  );

  const getNextInProgressIndex = useCallback(
    (fromIdx) => {
      if (!sections.length) return -1;
      for (let i = fromIdx + 1; i < sections.length; i += 1) {
        const status = getStatusForSection(sections[i]);
        if (status === "passed") continue;
        if (isBlockedStatus(status)) continue;
        return i;
      }
      return -1;
    },
    [sections, getStatusForSection, isBlockedStatus]
  );

  // Auto-advance to next section once current is fully passed
  useEffect(() => {
    if (!canAccess) return;
    if (!sections.length) return;
    if (!activeSection) return;
    const sid = String(activeSection.id || "");
    if (!sid) return;

    const prevStatus = lastStatusBySectionRef.current?.[sid];
    if (activeStatus !== "passed") {
      lastStatusBySectionRef.current = { ...(lastStatusBySectionRef.current || {}), [sid]: activeStatus };
      return;
    }

    // If this section was already passed before, don't auto-advance on revisit
    if (prevStatus === "passed") return;

    const nextIdx = getNextInProgressIndex(activeSectionIndex);
    if (nextIdx === -1) return;
    if (nextIdx === activeSectionIndex) return;

    setActiveSectionIndex(nextIdx);
    lastStatusBySectionRef.current = { ...(lastStatusBySectionRef.current || {}), [sid]: "passed" };
  }, [
    canAccess,
    sections,
    activeSection,
    activeStatus,
    completedSectionIds,
    activeSectionIndex,
    getNextInProgressIndex,
  ]);

  /** Progressive reveal (same as prep viewer) */
  const [visibleBlocksCount, setVisibleBlocksCount] = useState(1);

  const handleShowNextBlock = () => {
    if (!activeSection) return;
    const totalBlocks = blocks.length;
    setVisibleBlocksCount((prev) => (prev < totalBlocks ? prev + 1 : prev));
  };

  const goToNextSection = useCallback(() => {
    if (!sections.length) return;
    if (!activeSection) return;
    const currentIdx = sections.findIndex((s) => String(s.id) === String(activeSection.id));
    const nextIdx = getNextInProgressIndex(currentIdx);
    if (nextIdx === -1) return;
    setActiveSectionIndex(nextIdx);
  }, [sections, activeSection, getNextInProgressIndex]);

  const hasNextInProgress = useMemo(() => {
    if (!sections.length) return false;
    if (!activeSection) return false;
    const currentIdx = sections.findIndex((s) => String(s.id) === String(activeSection.id));
    return getNextInProgressIndex(currentIdx) !== -1;
  }, [sections, activeSection, getNextInProgressIndex]);

  const handleContinueNext = useCallback(() => {
    if (!hasNextInProgress) {
      window.location.hash = "#/dashboard";
      return;
    }
    goToNextSection();
  }, [hasNextInProgress, goToNextSection]);

  /** Lesson modal */
  const [mainOpen, setMainOpen] = useState(false);
  const openMain = () => setMainOpen(true);
  const closeMain = () => setMainOpen(false);

  /** Lightbox */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const isBooting = !enrollment || !enrollment._id;

  const mediaItems = useMemo(() => {
    const items = [];
    blocks.forEach((b) => {
      if (b?.type === "image" && b?.url) items.push({ type: "image", src: b.url, alt: b.alt || "" });
      if (b?.type === "video" && b?.url) {
        items.push({ type: "video", src: b.url, alt: b.alt || "Video", muted: !!b?.muted });
      }
    });
    return items;
  }, [blocks]);

  const submissionMediaItems = useMemo(() => {
    const items = [];
    const media = activeProgress?.submission?.media || [];

    media.forEach((m) => {
      const fileType = String(m?.fileType || "");
      const isImage = fileType.startsWith("image/");
      const isVideo = fileType.startsWith("video/");

      if (isImage) items.push({ type: "image", src: m.url, alt: m.name || "Submission image" });
      if (isVideo) items.push({ type: "video", src: m.url, alt: m.name || "Submission video" });
    });

    return items;
  }, [activeProgress?.submission?.media]);
  const reviewMediaItems = useMemo(() => {
    const items = [];
    const media = activeProgress?.review?.media || [];

    media.forEach((m) => {
      const fileType = String(m?.fileType || "");
      const isImage = fileType.startsWith("image/");
      const isVideo = fileType.startsWith("video/");

      if (isImage) items.push({ type: "image", src: m.url, alt: m.name || "Trainer feedback image" });
      if (isVideo) items.push({ type: "video", src: m.url, alt: m.name || "Trainer feedback video" });
    });

    return items;
  }, [activeProgress?.review?.media]);

  const openLightbox = useCallback((items, startIndex = 0) => {
    if (!items?.length) return;
    setLightboxItems(items);
    setLightboxIndex(startIndex);
    setLightboxOpen(true);
  }, []);

  const closeLightbox = useCallback(() => setLightboxOpen(false), []);
  const goPrev = useCallback(
    () => setLightboxIndex((i) => (i - 1 + (lightboxItems?.length || 1)) % (lightboxItems?.length || 1)),
    [lightboxItems]
  );
  const goNext = useCallback(
    () => setLightboxIndex((i) => (i + 1) % (lightboxItems?.length || 1)),
    [lightboxItems]
  );

  const openLightboxForSrc = (src) => {
    if (!src) return;
    const idx = mediaItems.findIndex((it) => it.src === src);
    openLightbox(mediaItems, idx >= 0 ? idx : 0);
  };

  const openSubmissionLightbox = useCallback(
    (startIndex = 0) => {
      if (!submissionMediaItems?.length) return;
      openLightbox(submissionMediaItems, startIndex);
    },
    [submissionMediaItems, openLightbox]
  );

  const enforceMuted = useCallback((e) => {
    const el = e?.currentTarget;
    if (!el) return;
    el.muted = true;
    el.volume = 0;
  }, []);

  // ✅ Assignment detection + safe defaults
  const assignment = activeSection?.assignment || null;
  const assignmentMinFiles = Number.isFinite(Number(assignment?.minFiles)) ? Number(assignment.minFiles) : 1;
  const assignmentMaxFiles = Number.isFinite(Number(assignment?.maxFiles)) ? Number(assignment.maxFiles) : 8;
  const assignmentRequiresText = !!assignment?.requiresText;
  const hasAssignment = !!assignment && assignmentMinFiles > 0;

  // ✅ Quiz
  const quiz = activeSection?.quiz || null;
  const quizQuestions = useMemo(() => {
    const qs = quiz?.questions;
    return Array.isArray(qs) ? qs : [];
  }, [quiz?.questions]);

  const hasQuiz = quizQuestions.length > 0;
  const quizPassed = !!activeProgress?.quiz?.passed;
  const [localQuizPassBySection, setLocalQuizPassBySection] = useState({});
  const effectiveQuizPassed =
    quizPassed || isChangesRequested || (activeSection?.id && localQuizPassBySection[String(activeSection.id)]);
  const canShowQuiz = hasQuiz && (visibleBlocksCount >= blocks.length || effectiveQuizPassed);

  useEffect(() => {
    if (effectiveQuizPassed) {
      setVisibleBlocksCount(blocks.length || 0);
      return;
    }
    setVisibleBlocksCount(blocks.length ? 1 : 0);
  }, [activeSection?.id, blocks.length, effectiveQuizPassed]);

  // Persist quiz passes so changes_requested doesn't force a retake
  useEffect(() => {
    const enrollmentId = enrollment?._id ? String(enrollment._id) : "";
    const sectionId = activeSection?.id ? String(activeSection.id) : "";
    if (!enrollmentId || !sectionId) return;

    const key = `pc-quiz-pass:${enrollmentId}:${sectionId}`;
    if (quizPassed) {
      try {
        localStorage.setItem(key, "1");
      } catch {
        // ignore storage errors
      }
      setLocalQuizPassBySection((prev) => ({
        ...(prev || {}),
        [sectionId]: true,
      }));
      return;
    }

    if (!localQuizPassBySection[sectionId]) {
      try {
        const stored = localStorage.getItem(key);
        if (stored === "1") {
          setLocalQuizPassBySection((prev) => ({
            ...(prev || {}),
            [sectionId]: true,
          }));
        }
      } catch {
        // ignore storage errors
      }
    }
  }, [enrollment?._id, activeSection?.id, quizPassed, localQuizPassBySection]);

  // ✅ Auto-pass sections
  const isAutoPassSection = !!activeSection?.autoPass;

  /** Scroll lock for modal + lightbox */
  useEffect(() => {
    const shouldLock = mainOpen || lightboxOpen;
    document.body.classList.toggle("modal-open", shouldLock);
    return () => document.body.classList.remove("modal-open");
  }, [mainOpen, lightboxOpen]);

  /** ESC closes (lightbox -> lesson) + arrows in lightbox */
  useEffect(() => {
    if (!mainOpen && !lightboxOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (lightboxOpen) closeLightbox();
        else closeMain();
      }
      if (lightboxOpen) {
        if (e.key === "ArrowLeft") goPrev();
        if (e.key === "ArrowRight") goNext();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [mainOpen, lightboxOpen, closeLightbox, goPrev, goNext]);

  /** ============================
   *  SUBMISSION UI (student/staff)
   *  ============================ */
  const [submissionText, setSubmissionText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);
  const assignmentRef = useRef(null);
  const didAutoScrollAssignmentRef = useRef({});
  const [assignmentToast, setAssignmentToast] = useState(false);
  const toastTimeoutRef = useRef(null);

  /** Staff review UI */
  const [reviewFeedback, setReviewFeedback] = useState("");
  const mainScrollRef = useRef(null);

  useEffect(() => {
    setSubmissionText("");
    setFiles([]);
    setReviewFeedback("");

    if (activeSection?.id) {
      delete didAutoScrollAssignmentRef.current[String(activeSection.id)];
    }
    setAssignmentToast(false);
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = null;
    }
  }, [activeSection?.id, activeProgress?.submission?.submittedAt]);

  // Reset scroll to top when switching sections
  useEffect(() => {
    if (!mainOpen) return;
    const el = mainScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: 0, left: 0, behavior: "instant" });
    });
  }, [activeSection?.id, mainOpen]);

  useEffect(() => {
    if (!mainOpen) return;
    if (!hasAssignment) return;
    if (!effectiveQuizPassed) return;
    if (isStaff) return;

    const sectionKey = activeSection?.id ? String(activeSection.id) : "";
    if (!sectionKey) return;
    if (didAutoScrollAssignmentRef.current[sectionKey]) return;

    const el = assignmentRef.current;
    if (el) {
      didAutoScrollAssignmentRef.current[sectionKey] = true;
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }
  }, [mainOpen, hasAssignment, effectiveQuizPassed, isStaff, activeSection?.id]);

  const handleAddFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;

    const merged = [...files, ...list].slice(0, assignmentMaxFiles);
    setFiles(merged);
    e.target.value = "";
  };

  const removeFileAt = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmitAssignment = async () => {
    if (!enrollment?._id || !activeSection?.id) return;
    if (!onSubmitSectionAssignment) return;

    // If section has a quiz, quiz must be passed before submitting assignment
    if (hasQuiz && !effectiveQuizPassed) {
      alert("Please pass the quiz first to unlock assignment submission.");
      return;
    }

    if (files.length < assignmentMinFiles) {
      alert(`Please upload at least ${assignmentMinFiles} file(s) before submitting.`);
      return;
    }

    if (assignmentRequiresText && !String(submissionText || "").trim()) {
      alert("Please include the required text before submitting.");
      return;
    }

    setBusy(true);
    try {
      await onSubmitSectionAssignment(enrollment._id, activeSection.id, {
        text: submissionText,
        files,
      });

      if (onRefreshEnrollments) await onRefreshEnrollments();
      setSubmissionText("");
      setFiles([]);
    } catch (e) {
      console.error(e);
      alert("Submission failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleReview = async (action) => {
    if (!enrollment?._id || !activeSection?.id) return;
    if (!onReviewSectionAssignment) return;

    setBusy(true);
    try {
      await onReviewSectionAssignment(enrollment._id, activeSection.id, {
        action, // "approve" | "request_changes"
        feedback: reviewFeedback,
      });

      if (onRefreshEnrollments) await onRefreshEnrollments();
      setReviewFeedback("");
    } catch (e) {
      console.error(e);
      alert("Review action failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const handleMarkPassed = async () => {
    if (!enrollment?._id || !activeSection?.id) return;
    if (!onMarkSectionPassed) {
      alert("Mark-passed handler not wired yet.");
      return;
    }

    // If there is a quiz, require it first
    if (hasQuiz && !effectiveQuizPassed) {
      alert("Please pass the quiz first.");
      return;
    }

    setBusy(true);
    try {
      await onMarkSectionPassed(enrollment._id, activeSection.id);
      if (onRefreshEnrollments) await onRefreshEnrollments();
    } catch (e) {
      console.error(e);
      alert("Could not mark as passed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  /** ============================
   *  AUTO-PASS
   *  ============================ */
  const didAutoPassRef = useRef({}); // sectionId => true

  useEffect(() => {
    if (!canAccess) return;
    if (isStaff) return;
    if (!activeSection?.id) return;
    if (!isAutoPassSection) return;
    if (!onMarkSectionPassed) return;

    const sid = String(activeSection.id);
    const already = !!didAutoPassRef.current[sid];
    const alreadyPassed = String(activeProgress?.status || "").toLowerCase() === "passed";
    if (already || alreadyPassed) return;

    didAutoPassRef.current[sid] = true;

    (async () => {
      try {
        await onMarkSectionPassed(enrollment._id, sid);
        if (onRefreshEnrollments) await onRefreshEnrollments();
      } catch (e) {
        console.warn("autoPass mark failed:", e);
        didAutoPassRef.current[sid] = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, isStaff, activeSection?.id, isAutoPassSection]);

  /** ============================
   *  QUIZ-ONLY PASS RULE
   *  If a section has a quiz and NO assignment:
   *   - passing the quiz counts as "passed" for the section
   *   - we mark passed automatically (student only) so Apprenticeship unlock logic works.
   *  ============================ */
  const didQuizPassMarkRef = useRef({}); // sectionId => true
  useEffect(() => {
    if (!canAccess) return;
    if (isStaff) return;
    if (!activeSection?.id) return;
    if (!hasQuiz) return;
    if (hasAssignment) return;
    if (!effectiveQuizPassed) return;
    if (!onMarkSectionPassed) return;

    const sid = String(activeSection.id);
    const alreadyPassed = String(activeProgress?.status || "").toLowerCase() === "passed";
    if (alreadyPassed) return;

    if (didQuizPassMarkRef.current[sid]) return;
    didQuizPassMarkRef.current[sid] = true;

    (async () => {
      try {
        await onMarkSectionPassed(enrollment._id, sid);
        if (onRefreshEnrollments) await onRefreshEnrollments();
      } catch (e) {
        console.warn("quiz-only mark passed failed:", e);
        didQuizPassMarkRef.current[sid] = false;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccess, isStaff, activeSection?.id, hasQuiz, hasAssignment, effectiveQuizPassed]);

  /** ============================
   *  QUIZ SUBMIT HANDLER
   *  ============================ */
  const handleSubmitQuizAttempt = async (payload) => {
    if (!enrollment?._id || !activeSection?.id) return;
    if (!onSubmitPostCourseQuizAttempt) {
      throw new Error("onSubmitPostCourseQuizAttempt not wired");
    }

    await onSubmitPostCourseQuizAttempt(enrollment._id, activeSection.id, payload);
    if (onRefreshEnrollments) await onRefreshEnrollments();
  };

  /** ============================
   *  EMPTY/LOCKED MODULE STATE
   *  ============================ */
  if (!module) {
    return (
      <div className="ink-messages-shell course-shell">
        <div className="course-shell-header">
          <div className="course-brand">
            <div className="course-brand-text">
              <div className="course-brand-tagline">{heroTag}</div>
              <h1 className="course-brand-title">{courseTitle}</h1>
            </div>
          </div>

          <div className="course-header-meta">
            <span className="course-header-chip">Signed in as {learnerName}</span>
            <Link to="/dashboard" className="course-header-link">
              ← Back to dashboard
            </Link>
          </div>
        </div>

        <div className="empty-chat-state lesson-empty-state">
          <h3>Post-course module not set up yet</h3>
          <p className="muted">We’ll seed this course’s post-class curriculum soon.</p>
        </div>
      </div>
    );
  }

  /** ============================
   *  RENDER
   *  ============================ */
  return (
    <div className="ink-messages-shell course-shell">
      <div className="course-shell-header">
        <div className="course-brand">
          <div className="course-brand-text">
            <div className="course-brand-tagline">{heroTag}</div>
            <h1 className="course-brand-title">{courseTitle}</h1>
          </div>
        </div>

        <div className="course-header-meta">
          <span className="course-header-chip">{canAccess ? "Unlocked" : "Locked"}</span>
          <span className="course-header-chip course-header-chip-accent">Signed in as {learnerName}</span>
          <Link to="/dashboard" className="course-header-link">
            ← Back to dashboard
          </Link>
        </div>
      </div>

      <div className="master-messages">
        {/* =========================
            LEFT SIDEBAR
           ========================= */}
        <div className={`master-messages-sidebar ${sidebarOpen ? "sidebar-open" : "sidebar-collapsed"}`}>
          <div className="course-section-sidebar-header">
            <h2>Your Post-Course Journey</h2>

            {totalSections > 0 && (
              <button type="button" className="course-section-toggle" onClick={() => setSidebarOpen((o) => !o)}>
                {sidebarOpen ? "Hide sections" : "Show sections"}
              </button>
            )}
          </div>

          {/* Coach orb + explainer (prep-style) */}
          <div className="prep-journey-coach" style={{ marginTop: 0 }}>
            <div className="prep-journey-orb">
              <img
                src={INSTRUCTORS.nikky.image}
                alt={INSTRUCTORS.nikky.name}
                className="prep-journey-orb-img"
              />
              <span className="prep-journey-orb-ring" />
            </div>

            <div className="prep-journey-coach-copy">
              <div className="prep-journey-coach-label">{INSTRUCTORS.nikky.name}</div>
              <div className="prep-journey-coach-title">How to complete your post-course</div>
              <div className="prep-journey-coach-body">
                <ul className="prep-journey-steps">
                  <li>These sections review what we learned together in class.</li>
                  <li>Some sections require a quiz and/or an assignment submission.</li>
                  <li>
                    You can navigate freely — even if you’re waiting on approval, keep moving forward and practicing.
                  </li>
                  <li>
                    To unlock <strong>Apprenticeship Mode</strong>, every section must show <strong>Passed</strong>.
                  </li>
                </ul>
              </div>
              <div className="prep-journey-coach-footer">
                <span className="prep-journey-coach-pill">Keep learning while we review</span>
              </div>
            </div>
          </div>

          <div className="course-section-overview">
            <div className="course-progress-pill">
              {completedCount}/{totalSections} sections passed
            </div>
            <div className="course-progress-bar-outer">
              <div className="course-progress-bar-inner" style={{ width: `${overallProgressPct}%` }} />
            </div>

            {isBooting ? (
              <div className="prep-journey-coach" style={{ marginTop: 12 }}>
                <div className="prep-journey-coach-copy">
                  <div className="prep-journey-coach-title">Loading your module…</div>
                  <div className="prep-journey-coach-body">
                    <div className="pc-loading-bar" />
                    <p className="muted" style={{ marginTop: 10, marginBottom: 0 }}>
                      Syncing your enrollments & progress.
                    </p>
                  </div>
                </div>
              </div>
            ) : !unlocked ? (
              <div className="prep-journey-coach" style={{ marginTop: 12 }}>
                <div className="prep-journey-coach-copy">
                  <div className="prep-journey-coach-title">Locked until training day</div>
                  <div className="prep-journey-coach-body">
                    <p className="muted" style={{ margin: 0 }}>
                      {unlockCopy}
                    </p>
                  </div>
                  <div className="prep-journey-coach-footer">
                    <span className="prep-journey-coach-pill">Unlocks on Day 1</span>
                  </div>
                </div>
              </div>
            ) : !prepCompleted ? (
              <div className="prep-journey-coach" style={{ marginTop: 12 }}>
                <div className="prep-journey-coach-copy">
                  <div className="prep-journey-coach-title">Complete prep to unlock</div>
                  <div className="prep-journey-coach-body">
                    <p className="muted" style={{ margin: 0 }}>
                      {prepCopy}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              !sidebarOpen && (
                <div className="course-start-row" style={{ marginTop: 12 }}>
                  <button
                    type="button"
                    className="course-start-button"
                    onClick={() => {
                      openMain();
                    }}
                    disabled={!activeSection}
                  >
                    {isActivePassed ? "Review section →" : "Open section →"}
                  </button>
                </div>
              )
            )}
          </div>

          <ul className="thread-list course-section-list">
            {sections.map((s, idx) => {
              const isActive = idx === activeSectionIndex;
              const locked = !canAccess;

              const prog = progressMap[String(s.id)];
              const isCompleted = String(prog?.status || "").toLowerCase() === "passed";
              const isChangesRequested = String(prog?.status || "").toLowerCase() === "changes_requested";

              const statusRaw = String(prog?.status || "in_progress").toLowerCase();
              const statusLabel = locked
                ? !unlocked
                  ? "Locked until Day 1"
                  : "Complete prep to unlock"
                : isCompleted
                ? "Passed"
                : getStatusLabel(statusRaw);

              return (
                <li key={s.id} className={isActive ? "section-item-active" : ""}>
                  <button
                    type="button"
                    className={[
                      "thread-item",
                      "course-section-item",
                      isActive && "active",
                      locked && "locked",
                      isCompleted && "completed",
                      isChangesRequested && "changes-requested",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onClick={() => {
                      if (locked) return;
                      didAutoSelectRef.current = true;
                      setActiveSectionIndex(idx);
                    }}
                    aria-current={isActive ? "step" : undefined}
                    aria-disabled={locked}
                  >
                    <div className="course-section-step-icon">
                      <span className="course-section-step-number">{s.index}</span>
                      {isCompleted && <span className="course-section-step-check">✓</span>}
                    </div>

                    <div className="thread-info">
                      <div className="thread-name">{s.title}</div>
                      <div className="thread-meta">
                        {statusRaw === "submitted" ? (
                          <span>
                            <span>Submitted — </span>
                            <span className="course-status-awaiting">awaiting review</span>
                          </span>
                        ) : (
                          statusLabel
                        )}
                      </div>
                    </div>
                  </button>

                  {sidebarOpen && canAccess && isActive && (
                    <div className="course-start-under-section">
                      <button
                        type="button"
                        className="course-start-button course-start-button--inline"
                        onClick={() => openMain()}
                      >
                        {isActivePassed ? "Review section →" : "Start section →"}
                      </button>

                      {mainOpen && (
                        <button
                          type="button"
                          className="course-start-button course-start-button--ghost course-start-button--inline"
                          onClick={closeMain}
                        >
                          Close lesson
                        </button>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>

        {/* =========================
            LESSON MODAL
           ========================= */}
        {mainOpen && <div className="course-main-backdrop open" onClick={closeMain} />}

        <div className={`course-main-modal ${mainOpen ? "open" : ""}`} role="dialog" aria-modal="true">
          <div className="course-main-modal-header">
            <div className="course-main-modal-title">
              {activeSection ? `Section ${activeSection.index}: ${activeSection.title}` : courseTitle}
            </div>

            <button type="button" className="course-main-modal-close" onClick={closeMain} aria-label="Close">
              ✕
            </button>
          </div>

        <div className="master-messages-chat course-main" ref={mainScrollRef}>
            {isBooting ? (
              <div className="empty-chat-state lesson-empty-state">
                <h3>Loading…</h3>
                <div className="pc-loading-bar" style={{ marginTop: 10 }} />
              </div>
            ) : !unlocked ? (
              <div className="empty-chat-state lesson-empty-state">
                <h3>Locked until Day 1</h3>
                <p className="muted">{unlockCopy}</p>
              </div>
            ) : !prepCompleted ? (
              <div className="empty-chat-state lesson-empty-state">
                <h3>Complete prep to unlock</h3>
                <p className="muted">{prepCopy}</p>
              </div>
            ) : !activeSection ? (
              <div className="empty-chat-state lesson-empty-state">
                <h3>Start with Section 1</h3>
                <p className="muted">Your post-course assignments will guide you from “class day” to real mastery.</p>
              </div>
            ) : (
              <div className="section-panel">
                {/* HERO */}
                <div className="instructor-hero">
                  <div className="hero-top-meta">
                    <div className="hero-breadcrumb">
                      <span className="hero-breadcrumb-course">{courseTitle}</span>
                      <span className="hero-breadcrumb-separator">/</span>
                      <span className="hero-breadcrumb-section">
                        Section {activeSection.index}: {activeSection.title}
                      </span>
                    </div>

                    {isComplete && (
                      <div className="course-complete-banner hero-top-banner">
                        <div className="course-complete-title">Post-course complete</div>
                        <div className="course-complete-sub">
                          You’ve passed every post-course section. Next step: <strong>Apprenticeship</strong>.
                        </div>
                        <Link
                          to="/dashboard"
                          className="course-header-link"
                          style={{ marginTop: 10, display: "inline-block" }}
                        >
                          Go to dashboard →
                        </Link>
                      </div>
                    )}
                  </div>

                  {/* Orb inside lesson (per-section instructor) */}
                  <div className="instructor-hero-main">
                    <div className="instructor-avatar-orb">
                      <div className="instructor-avatar-face instructor-avatar-face--image">
                        <img
                          src={activeInstructor.image}
                          alt={activeInstructor.name}
                          className="instructor-avatar-img"
                        />
                      </div>
                      <div className="instructor-avatar-ring" />
                    </div>

                    <div className="instructor-hero-copy">
                      <div className="instructor-hero-label">{activeInstructor.name}</div>
                      <div className="instructor-hero-headline">Post-course review, {learnerName}.</div>
                      <div className="instructor-hero-speech">
                        {heroSpeech.split("PASSED").map((part, idx, arr) => {
                          if (idx === arr.length - 1) return part;
                          return (
                            <React.Fragment key={`hero-speech-${idx}`}>
                              {part}
                              <strong>PASSED</strong>
                            </React.Fragment>
                          );
                        })}
                      </div>

                      <div style={{ marginTop: 10 }}>
                        <span className="prep-journey-coach-pill">{getStatusLabel(activeStatus)}</span>
                        {isAutoPassSection && (
                          <span className="prep-journey-coach-pill" style={{ marginLeft: 8 }}>
                            Auto-pass
                          </span>
                        )}
                        {hasQuiz && (
                          <span className="prep-journey-coach-pill" style={{ marginLeft: 8 }}>
                            Quiz {effectiveQuizPassed ? "passed" : "required"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* BODY BLOCKS (timeline) */}
                <div className="lesson-timeline">
                  {blocks.slice(0, visibleBlocksCount).map((b, i) => {
                    const isLastVisible = i === visibleBlocksCount - 1;

                    if (b?.type === "text") {
                      return (
                        <div key={i} className="lesson-block lesson-block-text">
                          <div className="lesson-block-timeline-dot" />
                          <div className="lesson-block-content">
                            <div className="lesson-block-body">
                              {isLastVisible ? <AnimatedText text={b.text} delay={0} /> : b.text}
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (b?.type === "image") {
                      return (
                        <div key={i} className="lesson-block lesson-block-media">
                          <div className="lesson-block-timeline-dot" />
                          <div className="lesson-block-content">
                            <button
                              type="button"
                              className="lesson-media-zoomable"
                              onClick={() => openLightboxForSrc(b.url)}
                              aria-label="Tap to view larger"
                            >
                              <div className="lesson-block-media-frame">
                                <img src={b.url} alt={b.alt || ""} className="lesson-block-image" />
                              </div>

                              <div className="lesson-media-zoom-cue">
                                <span className="lesson-media-zoom-text">Tap to zoom</span>
                                <span className="lesson-media-zoom-icon">🔍</span>
                              </div>
                            </button>
                          </div>
                        </div>
                      );
                    }

                    if (b?.type === "video") {
                      return (
                        <div key={i} className="lesson-block lesson-block-media">
                          <div className="lesson-block-timeline-dot" />
                          <div className="lesson-block-content">
                            <div className="lesson-block-media-frame lesson-block-media-frame--video">
                              <video
                                src={b.url}
                                className={`lesson-block-video ${b?.muted ? "video-no-volume" : ""}`}
                                controls
                                playsInline
                                muted={!!b?.muted}
                                onVolumeChange={b?.muted ? enforceMuted : undefined}
                                onLoadedMetadata={b?.muted ? enforceMuted : undefined}
                              />
                              <button
                                type="button"
                                className="lesson-video-fs"
                                onClick={() => openLightboxForSrc(b.url)}
                                aria-label="Open video larger"
                                title="Open larger"
                              >
                                ⛶
                              </button>
                            </div>

                            <div className="lesson-media-zoom-cue">
                              <span className="lesson-media-zoom-text">Tap ⛶ for fullscreen</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    // ✅ Resource blocks
                    if (b?.type === "resource" && b?.url) {
                      const label = safeUrlLabel(b?.label, b.url);
                      return (
                        <div key={i} className="lesson-block lesson-block-text">
                          <div className="lesson-block-timeline-dot" />
                          <div className="lesson-block-content">
                            <div className="quiz-instructor-note" style={{ margin: 0 }}>
                              <div style={{ fontWeight: 800, marginBottom: 6 }}>Resource</div>
                              <a
                                href={b.url}
                                target="_blank"
                                rel="noreferrer"
                                className="course-header-link"
                                style={{ display: "inline-block" }}
                              >
                                {label} →
                              </a>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>

                {visibleBlocksCount < blocks.length && (
                  <div className="course-continue-row course-continue-row-alt">
                    <div className="typing-indicator">
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-dot" />
                      <span className="typing-text">I have one more insight for you…</span>
                    </div>
                    <button type="button" className="course-continue-button" onClick={handleShowNextBlock}>
                      Show me the next piece →
                    </button>
                  </div>
                )}

                {assignmentToast && (
                  <div className="course-toast" role="status" aria-live="polite">
                    Unlocked — scroll to assignment.
                  </div>
                )}

                {/* QUIZ (prep-style markup/classes) */}
                {canShowQuiz && (
                  <SectionQuiz
                    sectionId={String(activeSection.id)}
                    quiz={quiz}
                    quizQuestions={quizQuestions}
                    quizPassed={effectiveQuizPassed}
                    learnerName={learnerName}
                    busy={busy}
                    onSubmitAttempt={async (payload) => {
                      setBusy(true);
                      try {
                        await handleSubmitQuizAttempt(payload);
                      } finally {
                        setBusy(false);
                      }
                    }}
                    onPassed={() => {
                      if (activeSection?.id) {
                        const sectionId = String(activeSection.id);
                        const enrollmentId = enrollment?._id ? String(enrollment._id) : "";
                        setLocalQuizPassBySection((prev) => ({
                          ...(prev || {}),
                          [sectionId]: true,
                        }));
                        if (enrollmentId) {
                          try {
                            localStorage.setItem(`pc-quiz-pass:${enrollmentId}:${sectionId}`, "1");
                          } catch {
                            // ignore storage errors
                          }
                        }
                      }
                      if (!hasAssignment || isStaff) return;
                      const el = assignmentRef.current;
                      if (el) {
                        requestAnimationFrame(() => {
                          el.scrollIntoView({ behavior: "smooth", block: "start" });
                        });
                      }
                      setAssignmentToast(true);
                      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
                      toastTimeoutRef.current = setTimeout(() => {
                        setAssignmentToast(false);
                        toastTimeoutRef.current = null;
                      }, 2600);
                    }}
                  />
                )}
                {hasQuiz && !canShowQuiz && (
                  <div className="quiz-instructor-note" style={{ marginTop: 12 }}>
                    Finish the lesson notes above to unlock the quiz.
                  </div>
                )}

                {/* ASSIGNMENT + SUBMISSION */}
                {hasAssignment ? (
                  <div className="course-section-quiz" style={{ marginTop: 18 }} ref={assignmentRef}>
                    <div className="quiz-header">
                      <h3>{assignment?.title || "Assignment"}</h3>
                      <p>{assignment?.instructions || "Follow the instructions and submit your work."}</p>
                    </div>

                    {/* Existing submission display */}
                    {activeProgress?.submission?.submittedAt && (
                      <div className="quiz-instructor-note" style={{ marginBottom: 12 }}>
                        <div style={{ fontWeight: 700, marginBottom: 6 }}>Submission</div>

                        {activeProgress.submission?.text ? (
                          <div style={{ marginBottom: 8 }}>{activeProgress.submission.text}</div>
                        ) : null}

                        {Array.isArray(activeProgress.submission?.media) && activeProgress.submission.media.length > 0 && (
                          <div className="dash-photo-grid" style={{ marginTop: 12 }}>
                            {activeProgress.submission.media.map((m, idx) => {
                              const fileType = String(m?.fileType || "");
                              const isImage = fileType.startsWith("image/");
                              const isVideo = fileType.startsWith("video/");
                              const isPdf = fileType === "application/pdf";

                              const lightboxIndex = submissionMediaItems.findIndex((it) => it.src === m.url);

                              return (
                                <div key={idx} className="dash-photo-tile">
                                  {isImage ? (
                                    <button
                                      type="button"
                                      className="lesson-media-zoomable"
                                      onClick={() => openSubmissionLightbox(lightboxIndex >= 0 ? lightboxIndex : 0)}
                                      aria-label="Open submission image"
                                      style={{ padding: 0, border: "none", background: "transparent", width: "100%" }}
                                    >
                                      <img src={m.url} alt={m.name || ""} className="dash-photo-img" />
                                      <div className="lesson-media-zoom-cue">
                                        <span className="lesson-media-zoom-text">Tap to zoom</span>
                                        <span className="lesson-media-zoom-icon">🔍</span>
                                      </div>
                                    </button>
                                  ) : isVideo ? (
                                    <button
                                      type="button"
                                      className="lesson-media-zoomable"
                                      onClick={() => openSubmissionLightbox(lightboxIndex >= 0 ? lightboxIndex : 0)}
                                      aria-label="Open submission video"
                                      style={{ padding: 0, border: "none", background: "transparent", width: "100%" }}
                                    >
                                      <video src={m.url} className="dash-photo-img" playsInline muted />
                                      <div className="lesson-media-zoom-cue">
                                        <span className="lesson-media-zoom-text">Tap to zoom</span>
                                        <span className="lesson-media-zoom-icon">⛶</span>
                                      </div>
                                    </button>
                                  ) : (
                                    <a
                                      href={m.url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="media-tile"
                                      style={{ display: "grid", placeItems: "center" }}
                                    >
                                      <div className="media-tile-label">{m.name || (isPdf ? "PDF" : "File")}</div>
                                    </a>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}

                        <div className="muted" style={{ marginTop: 10 }}>
                          Submitted: {formatLocalDate(activeProgress.submission?.submittedAt)}
                        </div>

                        {activeProgress.review?.status && (
                          <div style={{ marginTop: 10 }}>
                            <span className="prep-journey-coach-pill">
                              Review: {activeProgress.review.status === "approved" ? "Approved" : "Changes requested"}
                            </span>
                          </div>
                        )}

                        {activeStatus === "submitted" && hasNextInProgress && (
                          <div className="quiz-footer" style={{ marginTop: 12 }}>
                            <button type="button" className="quiz-submit-button" onClick={handleContinueNext}>
                              Continue to next section →
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* STUDENT SUBMISSION COMPOSER */}
                    {!isStaff && activeStatus !== "passed" && (
                      <div className="quiz-question-card" style={{ marginBottom: 12 }}>
                        <div className="quiz-question-number">Your submission</div>

                        {hasQuiz && !effectiveQuizPassed ? (
                          <div className="dash-apprenticeship-lockline" style={{ marginTop: 10 }}>
                            Pass the quiz above to unlock submission.
                          </div>
                        ) : (
                          <>
                            {activeProgress?.review?.status === "changes_requested" &&
                              activeProgress?.review?.feedback && (
                                <div
                                  className="dash-apprenticeship-changes pc-trainer-notes"
                                  style={{ marginTop: 10, marginBottom: 10 }}
                                >
                                  <div className="dash-course-meta-strong pc-trainer-notes-label">
                                    Trainer notes for requested change
                                  </div>
                                  <div className="dash-apprenticeship-notes pc-trainer-notes-body">
                                    {activeProgress.review.feedback}
                                  </div>
                                  {reviewMediaItems.length > 0 ? (
                                    <div style={{ marginTop: 10 }}>
                                      <div className="dash-course-meta-strong" style={{ marginBottom: 8 }}>
                                        Trainer Reference Photos
                                      </div>
                                      <div className="dash-course-meta" style={{ marginBottom: 8, opacity: 0.78 }}>
                                        Tap any photo to zoom
                                      </div>
                                      <div className="pc-media-grid">
                                        {reviewMediaItems.map((m, idx) => (
                                          <button
                                            key={`review-media-change-${m.src}`}
                                            type="button"
                                            className="pc-media-thumb lesson-media-zoomable"
                                            onClick={() => openLightbox(reviewMediaItems, idx)}
                                          >
                                            <img src={m.src} alt={m.alt || "Trainer feedback"} loading="lazy" />
                                            <span className="lesson-media-zoom-cue" aria-hidden="true">
                                              <span className="lesson-media-zoom-icon">⌕</span>
                                            </span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              )}

                            {activeStatus === "submitted" ? (
                              <div className="dash-apprenticeship-lockline" style={{ marginTop: 10 }}>
                                Submitted — awaiting review (you can keep moving forward)
                              </div>
                            ) : (
                              <>
                                <div className="quiz-prompt" style={{ marginTop: 6, paddingTop: 10 }}>
                                  {assignmentRequiresText
                                    ? "Required: explain your work in the text box."
                                    : "Add a quick note about your work (optional)."}
                                </div>

                                <textarea
                                  className="dash-apprenticeship-textarea"
                                  placeholder="What did you do? Any questions for your trainer?"
                                  value={submissionText}
                                  onChange={(e) => setSubmissionText(e.target.value)}
                                  rows={4}
                                  style={{ width: "100%", marginTop: 8 }}
                                  disabled={busy}
                                />

                                <div style={{ marginTop: 12 }}>
                                  <div className="dash-course-meta dash-apprenticeship-count">
                                    Upload files ({files.length}/{assignmentMaxFiles})
                                  </div>

                                  <input
                                    ref={fileInputRef}
                                    type="file"
                                    multiple
                                    accept="image/*,video/*,application/pdf"
                                    className="dash-file-input"
                                    onChange={handleAddFiles}
                                    disabled={busy}
                                  />

                                  {files.length > 0 && (
                                    <div className="dash-photo-grid" style={{ marginTop: 10 }}>
                                      {files.map((file, i) => {
                                        const url = URL.createObjectURL(file);
                                        const isVideo = file.type?.startsWith("video/");
                                        const isPdf = file.type === "application/pdf";

                                        return (
                                          <div key={i} className="dash-photo-tile">
                                            {isPdf ? (
                                              <a
                                                href={url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="media-tile"
                                                style={{ display: "grid", placeItems: "center" }}
                                                onClick={() => setTimeout(() => URL.revokeObjectURL(url), 1500)}
                                              >
                                                <div className="media-tile-label">{file.name || "PDF"}</div>
                                              </a>
                                            ) : isVideo ? (
                                              <video
                                                src={url}
                                                className="dash-photo-img"
                                                controls
                                                playsInline
                                                onLoadedData={() => URL.revokeObjectURL(url)}
                                              />
                                            ) : (
                                              <img
                                                src={url}
                                                alt=""
                                                className="dash-photo-img"
                                                onLoad={() => URL.revokeObjectURL(url)}
                                              />
                                            )}

                                            <button
                                              type="button"
                                              className="dash-photo-remove"
                                              onClick={() => removeFileAt(i)}
                                              disabled={busy}
                                            >
                                              ✕
                                            </button>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>

                                <div className="quiz-footer" style={{ marginTop: 12 }}>
                                  {files.length < assignmentMinFiles && (
                                    <div className="quiz-error" style={{ marginBottom: 10 }}>
                                      Please upload at least {assignmentMinFiles} file
                                      {assignmentMinFiles === 1 ? "" : "s"}.
                                    </div>
                                  )}

                                  {assignmentRequiresText && !String(submissionText || "").trim() && (
                                    <div className="quiz-error" style={{ marginBottom: 10 }}>
                                      Please add the required text explanation.
                                    </div>
                                  )}

                                  <button
                                    type="button"
                                    className="quiz-submit-button"
                                    disabled={
                                      busy ||
                                      files.length < assignmentMinFiles ||
                                      (assignmentRequiresText && !String(submissionText || "").trim()) ||
                                      (hasQuiz && !effectiveQuizPassed)
                                    }
                                    onClick={handleSubmitAssignment}
                                  >
                                    {busy ? "Submitting…" : "Submit assignment"}
                                  </button>
                                </div>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    )}

                    {/* STAFF REVIEW CONTROLS */}
                    {isStaff && activeProgress?.submission?.submittedAt && activeStatus !== "passed" && (
                      <div className="quiz-question-card" style={{ marginTop: 18 }}>
                        <div className="quiz-question-number">Instructor review</div>

                        <textarea
                          className="dash-apprenticeship-textarea"
                          placeholder="Optional feedback for the artist…"
                          value={reviewFeedback}
                          onChange={(e) => setReviewFeedback(e.target.value)}
                          rows={3}
                          style={{ width: "100%", marginTop: 8 }}
                          disabled={busy}
                        />

                        <div className="quiz-footer" style={{ marginTop: 12 }}>
                          <button
                            type="button"
                            className="quiz-submit-button"
                            disabled={busy}
                            onClick={() => handleReview("approve")}
                          >
                            Approve section
                          </button>

                          <button
                            type="button"
                            className="quiz-submit-button quiz-submit-button--ghost"
                            disabled={busy}
                            onClick={() => handleReview("request_changes")}
                            style={{ marginLeft: 8 }}
                          >
                            Request changes
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  // No assignment: quiz-only sections pass by quiz (auto-mark), autoPass sections auto-pass
                  <div className="course-section-quiz" style={{ marginTop: 18 }}>
                    <div className="quiz-header">
                      <h3>Section status</h3>
                      <p>
                        {isAutoPassSection
                          ? "This is a review-only section and auto-passes."
                          : hasQuiz
                          ? "This section is quiz-based. Passing the quiz counts as Passed."
                          : "No quiz or assignment here. When you’re done reviewing, mark it passed."}
                      </p>
                    </div>

                    {activeStatus === "passed" ? (
                      <div className="dash-apprenticeship-lockline" style={{ marginTop: 10 }}>
                        Passed ✅
                      </div>
                    ) : !isStaff ? (
                      <div className="quiz-footer" style={{ marginTop: 12 }}>
                        <button
                          type="button"
                          className="quiz-submit-button"
                          disabled={busy || isAutoPassSection || (hasQuiz && !effectiveQuizPassed)}
                          onClick={handleMarkPassed}
                        >
                          {isAutoPassSection
                            ? "Auto-pass enabled"
                            : hasQuiz && !effectiveQuizPassed
                            ? "Pass quiz to unlock"
                            : busy
                            ? "Saving…"
                            : "Mark passed"}
                        </button>
                        {hasQuiz && effectiveQuizPassed && hasNextInProgress && (
                          <button
                            type="button"
                            className="quiz-submit-button quiz-submit-button--ghost"
                            onClick={handleContinueNext}
                            style={{ marginLeft: 8 }}
                          >
                            Continue to next section →
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="dash-apprenticeship-lockline" style={{ marginTop: 10 }}>
                        Waiting for artist to complete
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* =========================
            LIGHTBOX
           ========================= */}
        <Lightbox
          open={lightboxOpen}
          items={lightboxItems}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
          enforceMuted={enforceMuted}
        />
      </div>
    </div>
  );
};

export default PostCourseViewer;
